import isObject from "lodash/isObject";
import { TFunction, useTranslation } from "next-i18next";
import React from "react";
import ReactMarkdown from "react-markdown";

import styles from "./styles.module.css";

import {
  ImmigramFormElementConfigCheckbox,
  ImmigramFormElementConfigDate,
  ImmigramFormElementConfigHeading,
  ImmigramFormElementConfigHidden,
  ImmigramFormElementConfigParagraph,
  ImmigramFormElementConfigSelect,
  ImmigramFormElementConfigText,
  ImmigramFormElementConfigTextarea,
  ImmigramFormElementConfigToggle,
} from "./";

export type ImmigramFormElementOnChange = (
  field: string,
  value: string
) => void;

export interface ImmigramFormElementConfigBase {
  type: string;
}

export enum ImmigramFormElementType {
  Checkbox = "checkbox",
  Date = "date",
  Heading = "heading",
  Hidden = "hidden",
  Paragraph = "paragraph",
  Select = "select",
  Text = "text",
  Textarea = "textarea",
  Toggle = "toggle",
}

export interface ImmigramFormElementValidation {
  required?: boolean;
  minLength?: number;
  greaterThanRolling?: number;
}

export type ImmigramFormInputElementConfig =
  | ImmigramFormElementConfigTextarea
  | ImmigramFormElementConfigCheckbox
  | ImmigramFormElementConfigSelect
  | ImmigramFormElementConfigText
  | ImmigramFormElementConfigDate
  | ImmigramFormElementConfigHidden
  | ImmigramFormElementConfigToggle;
export type ImmigramFormElementConfig =
  | ImmigramFormElementConfigParagraph
  | ImmigramFormElementConfigHeading
  | ImmigramFormInputElementConfig;

type ParamMd = { md: string };
type ParamI18n = { i18n: string };
type ParamI18nMd = { i18n: { md: string } };
export type ConfigParamValueWithModifiers =
  | string
  | ParamMd
  | ParamI18n
  | ParamI18nMd;

export function getModifiedConfigParamValue(
  t: TFunction,
  paramValue?: ConfigParamValueWithModifiers,
  {
    forceText,
  }: {
    forceText?: boolean;
  } = {}
): React.ReactNode | string {
  if (!isObject(paramValue)) {
    return paramValue;
  }

  if ("i18n" in paramValue) {
    const paramI18n = paramValue as ParamI18n | ParamI18nMd;

    if (isObject(paramI18n.i18n) && "md" in paramI18n.i18n) {
      const paramMd = paramValue as ParamI18nMd;
      if (forceText) {
        return t(paramMd.i18n.md);
      }

      return (
        <ReactMarkdown linkTarget="_blank" className={styles.markdown}>
          {t(paramMd.i18n.md)}
        </ReactMarkdown>
      );
    }
    return t(paramI18n.i18n);
  }

  if ("md" in paramValue) {
    const paramMd = paramValue as ParamMd;

    if (forceText) {
      return paramMd.md;
    }

    return (
      <ReactMarkdown linkTarget="_blank" className={styles.markdown}>
        {paramMd.md}
      </ReactMarkdown>
    );
  }

  return paramValue;
}

export function useModifiedConfigParamValue(
  configParam?: ConfigParamValueWithModifiers
) {
  const { t } = useTranslation();

  return getModifiedConfigParamValue(t, configParam);
}
